import { useState } from 'react';
import { Button, Tooltip } from '@mui/material';
import { AddCircleOutline } from '@mui/icons-material';
import { usePermissions, useNotify, useRedirect, useTranslate } from 'react-admin';
import { hasPermission } from '../../utils/hasPermission';
import { apiClient } from '../../utils/apiClient';

// Minimal shape the report card / detail view already holds. The button needs at least an id
// (investigationId or id) and the status (REF-05 s.3 report payload / sub-plan 14 detail).
export interface PromotableInvestigation {
  investigationId?: string;
  id?: string;
  status?: string | null;
  rootCauseCategory?: string | null;
}

interface PromoteToPlaybookButtonProps {
  investigation: PromotableInvestigation;
}

// Promote-to-playbook action: POST investigations/:id/promote, then redirect into the new
// playbook's Edit screen for refinement. Hidden without ai_troubleshooting:manage:all; disabled
// (with a reason tooltip) when the investigation is not promotable (status refused/failed/running).
// A re-promote of an already-promoted source is NOT pre-disabled (there is no such flag on the
// payload); it is rejected by the backend with VALIDATION_FAILED (400) and surfaced via the catch.
export const PromoteToPlaybookButton = ({ investigation }: PromoteToPlaybookButtonProps) => {
  const { permissions } = usePermissions();
  const notify = useNotify();
  const redirect = useRedirect();
  const translate = useTranslate();
  const [loading, setLoading] = useState(false);

  const canManage = hasPermission(
    permissions as string[] | null | undefined,
    'ai_troubleshooting:manage:all',
  );
  if (!canManage) return null; // hidden entirely without manage perm

  const id = investigation.investigationId ?? investigation.id;
  const status = investigation.status;
  const promotable = status === 'completed';
  const disabledReason = !promotable
    ? status === 'refused'
      ? translate('resources.ai-playbooks.promote.disabledRefused')
      : status === 'failed'
        ? translate('resources.ai-playbooks.promote.disabledFailed')
        : status === 'running'
          ? translate('resources.ai-playbooks.promote.disabledRunning')
          : translate('resources.ai-playbooks.promote.disabledGeneric')
    : '';

  const onPromote = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res: any = await apiClient.post(
        `/api/backend/ai-troubleshooting/investigations/${id}/promote`,
        {},
      );
      // Sub-plan 15 returns { data: playbook } (the create unwrap allowlist reads response.data.id).
      const newId = res?.data?.id ?? res?.id;
      notify('resources.ai-playbooks.promote.success', { type: 'success' });
      if (newId) {
        redirect('edit', 'ai-playbooks', newId); // -> #/ai-playbooks/:id (Edit screen)
      } else {
        redirect('list', 'ai-playbooks');
      }
    } catch (e: any) {
      // Map the backend error_code (REF-07) to a TRANSLATED message (the `errors.*` namespace has no
      // AI_* keys, so notify('errors.AI_INVESTIGATION_NOT_FOUND') would leak the raw key). Codes:
      // AI_INVESTIGATION_NOT_FOUND (404, missing OR out-of-scope source — sub-plan 15 reads under the
      // acting admin's scope); VALIDATION_FAILED (400, a non-completed or already-promoted source);
      // PERMISSION_DENIED (403, defense-in-depth). No redirect on error.
      const code = e?.errorCode;
      let msg: string;
      if (code === 'AI_INVESTIGATION_NOT_FOUND') {
        msg = translate('resources.ai-playbooks.promote.errorNotFound', {
          _: 'That investigation no longer exists or is outside your scope.',
        });
      } else if (code === 'VALIDATION_FAILED') {
        msg =
          e?.message ||
          translate('resources.ai-playbooks.promote.errorNotPromotable', {
            _: 'This investigation cannot be promoted (already promoted or not completed).',
          });
      } else if (code === 'PERMISSION_DENIED') {
        msg = translate('resources.ai-playbooks.promote.errorPermission', {
          _: 'You do not have permission to promote investigations.',
        });
      } else {
        // Localized generic message only — never surface a raw JS/network error string.
        msg = translate('resources.ai-playbooks.promote.error', {
          _: 'Could not promote this investigation to a playbook.',
        });
      }
      notify(msg, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Tooltip title={disabledReason} disableHoverListener={promotable}>
      {/* span wrapper so the Tooltip still fires on a disabled Button */}
      <span>
        <Button
          variant="outlined"
          startIcon={<AddCircleOutline />}
          disabled={!promotable || loading}
          onClick={onPromote}
        >
          {translate('resources.ai-playbooks.promote.button')}
        </Button>
      </span>
    </Tooltip>
  );
};

export default PromoteToPlaybookButton;
